/**
 * LYT Communications - Assignment Manager
 * Version: 2.0.0
 * Created: 2026-02-23
 * Updated: 2026-05-11
 *
 * Displays assignment cards for contractors and subs.
 * Contractors: see pending assignments from admin, accept/decline, assign to subs.
 * Subs: see assignments from parent contractor, accept/decline.
 *
 * v2.0 Changes:
 * - Group assignments by status (Needs Action, Accepted, In Progress, Declined)
 * - Expandable segment chip list ("+X more" is now clickable)
 * - Decline reason moved to clean modal overlay
 */

import React, { useState, useCallback } from 'react';
import { CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, Users, AlertTriangle, Send, X } from 'lucide-react';

const STATUS_COLORS = {
  'Pending': '#FFB800',
  'Accepted': '#4CAF50',
  'Declined': '#FF4444',
  'Assigned to Sub': '#2196F3',
  'In Progress': '#4CAF50',
};

// Group config: order, label, which statuses belong
const STATUS_GROUPS = [
  { key: 'action', label: 'Needs Action', statuses: ['Pending', 'Assigned to Sub'], defaultOpen: true },
  { key: 'accepted', label: 'Accepted', statuses: ['Accepted'], defaultOpen: true },
  { key: 'inprogress', label: 'In Progress', statuses: ['In Progress'], defaultOpen: true },
  { key: 'declined', label: 'Declined', statuses: ['Declined'], defaultOpen: false },
];

function AssignmentManager({
  assignments = [],
  subs = [],
  darkMode,
  user,
  onAccept,
  onDecline,
  onAssignToSub,
  onSubAccept,
}) {
  const [expanded, setExpanded] = useState(true);
  const [declineId, setDeclineId] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [assignSubId, setAssignSubId] = useState(null);
  const [selectedSub, setSelectedSub] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedSegments, setExpandedSegments] = useState({});
  const [collapsedGroups, setCollapsedGroups] = useState({ declined: true });

  const isContractor = user?.role === 'contractor';
  const isSub = user?.role === 'sub';

  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';
  const accent = darkMode ? '#c850c0' : '#0077B6';
  const inputBg = darkMode ? '#0d1b2a' : '#ffffff';

  const handleAccept = useCallback(async (assignmentId) => {
    setActionLoading(assignmentId);
    if (isSub) {
      await onSubAccept?.(assignmentId);
    } else {
      await onAccept?.(assignmentId);
    }
    setActionLoading(null);
  }, [isSub, onAccept, onSubAccept]);

  const handleDecline = useCallback(async (assignmentId) => {
    setActionLoading(assignmentId);
    await onDecline?.(assignmentId, declineReason);
    setDeclineId(null);
    setDeclineReason('');
    setActionLoading(null);
  }, [onDecline, declineReason]);

  const handleAssignSub = useCallback(async (assignmentId) => {
    if (!selectedSub) return;
    setActionLoading(assignmentId);
    await onAssignToSub?.(assignmentId, selectedSub);
    setAssignSubId(null);
    setSelectedSub('');
    setActionLoading(null);
  }, [onAssignToSub, selectedSub]);

  const parseSegmentIds = (jsonStr) => {
    try { return JSON.parse(jsonStr || '[]'); }
    catch { return []; }
  };

  // Filter assignments based on role
  const visibleAssignments = assignments.filter(a => {
    if (isSub) return a.status === 'Assigned to Sub';
    if (isContractor) return ['Pending', 'Accepted', 'Assigned to Sub', 'In Progress', 'Declined'].includes(a.status);
    return true;
  });

  const totalActive = visibleAssignments.filter(a => a.status !== 'Declined').length;

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    border: `1px solid ${borderColor}`, background: inputBg, color: text,
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };

  const toggleSegmentExpansion = (id) => {
    setExpandedSegments(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const renderAssignmentCard = (asgn, idx) => {
    const segIds = parseSegmentIds(asgn.segment_ids);
    const statusColor = STATUS_COLORS[asgn.status] || '#9E9E9E';
    const isLoading = actionLoading === asgn.assignment_id;
    const isSegExpanded = expandedSegments[asgn.assignment_id];
    const isDeclined = asgn.status === 'Declined';

    return (
      <div key={asgn.assignment_id || idx} style={{
        padding: '12px', borderRadius: 8,
        border: `1px solid ${borderColor}`, marginBottom: 8,
        background: darkMode ? '#0d1b2a' : '#fff',
        opacity: isLoading ? 0.6 : isDeclined ? 0.5 : 1,
      }}>
        {/* Assignment header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: text }}>{asgn.assignment_id}</span>
            <span style={{
              fontSize: 10, padding: '2px 6px', borderRadius: 8, fontWeight: 600,
              background: `${statusColor}20`, color: statusColor,
            }}>{asgn.status}</span>
          </div>
          <span style={{
            fontSize: 10, padding: '2px 6px', borderRadius: 8, fontWeight: 600,
            background: darkMode ? '#1e3a5f' : '#e2e8f0', color: textMuted,
          }}>{asgn.work_type}</span>
        </div>

        {/* Details */}
        <div style={{ fontSize: 12, color: textMuted, marginBottom: 6 }}>
          <div>{segIds.length} segment{segIds.length !== 1 ? 's' : ''} — {asgn.project_id || 'Unknown project'}</div>
          {asgn.deadline && <div>Deadline: {asgn.deadline}</div>}
          {asgn.notes && <div style={{ fontStyle: 'italic', marginTop: 2 }}>{asgn.notes}</div>}
        </div>

        {/* Segment chips — expandable */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
          {(isSegExpanded ? segIds : segIds.slice(0, 8)).map(sid => (
            <span key={sid} style={{
              fontSize: 10, padding: '2px 6px', borderRadius: 4,
              background: darkMode ? '#1e3a5f' : '#e2e8f0', color: textMuted,
            }}>{sid}</span>
          ))}
          {segIds.length > 8 && (
            <button
              onClick={() => toggleSegmentExpansion(asgn.assignment_id)}
              style={{
                fontSize: 10, color: accent, cursor: 'pointer',
                background: 'none', border: 'none', padding: '2px 4px',
                fontWeight: 600,
              }}
            >
              {isSegExpanded ? 'Show less' : `+${segIds.length - 8} more`}
            </button>
          )}
        </div>

        {/* Sub assignment info */}
        {asgn.assigned_to_sub && (
          <div style={{ fontSize: 11, color: '#2196F3', marginBottom: 6 }}>
            Assigned to sub: {asgn.assigned_to_sub}
            {asgn.sub_accepted === 'true' && <span style={{ color: '#4CAF50' }}> (accepted)</span>}
          </div>
        )}

        {/* Decline reason display */}
        {isDeclined && asgn.declined_reason && (
          <div style={{ fontSize: 11, color: '#FF4444', fontStyle: 'italic', marginBottom: 6 }}>
            Reason: {asgn.declined_reason}
          </div>
        )}

        {/* Action buttons for contractors */}
        {isContractor && asgn.status === 'Pending' && (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              onClick={() => handleAccept(asgn.assignment_id)}
              disabled={isLoading}
              style={{
                flex: 1, padding: '8px', fontSize: 12, fontWeight: 600,
                background: '#4CAF50', color: '#fff', border: 'none',
                borderRadius: 6, cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', gap: 4,
              }}
            >
              <CheckCircle size={14} /> Accept
            </button>
            <button
              onClick={() => { setDeclineId(asgn.assignment_id); setDeclineReason(''); }}
              disabled={isLoading}
              style={{
                flex: 1, padding: '8px', fontSize: 12, fontWeight: 600,
                background: '#FF4444', color: '#fff', border: 'none',
                borderRadius: 6, cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', gap: 4,
              }}
            >
              <XCircle size={14} /> Decline
            </button>
          </div>
        )}

        {/* Assign to sub button (for contractors with accepted assignments) */}
        {isContractor && asgn.status === 'Accepted' && subs.length > 0 && !asgn.assigned_to_sub && (
          <div style={{ marginTop: 8 }}>
            {assignSubId === asgn.assignment_id ? (
              <div>
                <select value={selectedSub} onChange={e => setSelectedSub(e.target.value)} style={inputStyle}>
                  <option value="">Select sub-contractor...</option>
                  {subs.map(s => (
                    <option key={s.email} value={s.email}>{s.name} ({s.email})</option>
                  ))}
                </select>
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button
                    onClick={() => handleAssignSub(asgn.assignment_id)}
                    disabled={!selectedSub}
                    style={{ flex: 1, padding: '6px', fontSize: 11, fontWeight: 600, background: selectedSub ? '#2196F3' : '#9E9E9E', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                  ><Send size={12} /> Assign to Sub</button>
                  <button
                    onClick={() => { setAssignSubId(null); setSelectedSub(''); }}
                    style={{ flex: 1, padding: '6px', fontSize: 11, fontWeight: 600, background: 'transparent', color: textMuted, border: `1px solid ${borderColor}`, borderRadius: 6, cursor: 'pointer' }}
                  >Cancel</button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setAssignSubId(asgn.assignment_id)}
                style={{
                  width: '100%', padding: '6px', fontSize: 11, fontWeight: 600,
                  background: 'transparent', color: '#2196F3',
                  border: `1px dashed #2196F340`, borderRadius: 6, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                <Users size={12} /> Assign to Sub-Contractor
              </button>
            )}
          </div>
        )}

        {/* Sub accept/decline buttons */}
        {isSub && asgn.status === 'Assigned to Sub' && asgn.sub_accepted !== 'true' && (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              onClick={() => handleAccept(asgn.assignment_id)}
              disabled={isLoading}
              style={{
                flex: 1, padding: '8px', fontSize: 12, fontWeight: 600,
                background: '#4CAF50', color: '#fff', border: 'none',
                borderRadius: 6, cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', gap: 4,
              }}
            >
              <CheckCircle size={14} /> Accept
            </button>
            <button
              onClick={() => { setDeclineId(asgn.assignment_id); setDeclineReason(''); }}
              disabled={isLoading}
              style={{
                flex: 1, padding: '8px', fontSize: 12, fontWeight: 600,
                background: '#FF4444', color: '#fff', border: 'none',
                borderRadius: 6, cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', gap: 4,
              }}
            >
              <XCircle size={14} /> Decline
            </button>
          </div>
        )}

        {/* Timestamps */}
        <div style={{ fontSize: 10, color: textMuted, marginTop: 6 }}>
          {asgn.created_at && <span>Created: {new Date(asgn.created_at).toLocaleDateString()}</span>}
          {asgn.contractor_accepted_at && <span> | Accepted: {new Date(asgn.contractor_accepted_at).toLocaleDateString()}</span>}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 10,
      overflow: 'hidden', marginBottom: 8,
    }}>
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', cursor: 'pointer',
          borderBottom: expanded ? `1px solid ${borderColor}` : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Users size={16} color={accent} />
          <span style={{ fontWeight: 700, fontSize: 14, color: text }}>Work Assignments</span>
          <span style={{
            fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
            background: totalActive > 0 ? '#FFB80020' : '#4CAF5020',
            color: totalActive > 0 ? '#FFB800' : '#4CAF50',
          }}>
            {totalActive} assignment{totalActive !== 1 ? 's' : ''}
          </span>
        </div>
        {expanded ? <ChevronUp size={14} color={textMuted} /> : <ChevronDown size={14} color={textMuted} />}
      </div>

      {expanded && (
        <div style={{ padding: '10px 14px' }}>
          {visibleAssignments.length === 0 && (
            <div style={{ fontSize: 12, color: textMuted, textAlign: 'center', padding: '12px 0' }}>
              No assignments at this time.
            </div>
          )}

          {/* Grouped by status */}
          {STATUS_GROUPS.map(group => {
            const groupItems = visibleAssignments.filter(a => group.statuses.includes(a.status));
            if (groupItems.length === 0) return null;
            const isCollapsed = collapsedGroups[group.key];

            return (
              <div key={group.key} style={{ marginBottom: 12 }}>
                {/* Section header */}
                <div
                  onClick={() => toggleGroup(group.key)}
                  style={{
                    fontSize: '0.8rem', fontWeight: 600, color: textMuted,
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                    padding: '8px 0', borderBottom: `1px solid ${borderColor}`,
                    marginBottom: 8, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}
                >
                  <span>{group.label} ({groupItems.length})</span>
                  {isCollapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                </div>
                {!isCollapsed && groupItems.map((asgn, idx) => renderAssignmentCard(asgn, idx))}
              </div>
            );
          })}
        </div>
      )}

      {/* Decline Modal Overlay */}
      {declineId && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }} onClick={(e) => { if (e.target === e.currentTarget) { setDeclineId(null); setDeclineReason(''); } }}>
          <div style={{
            background: cardBg, borderRadius: 12, padding: 24, maxWidth: 400, width: '100%',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: text }}>Decline Assignment</h3>
              <button onClick={() => { setDeclineId(null); setDeclineReason(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMuted, padding: 4 }}>
                <X size={18} />
              </button>
            </div>

            <p style={{ fontSize: 13, color: textMuted, margin: '0 0 12px' }}>
              Please provide a reason for declining this assignment.
            </p>

            <textarea
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              placeholder="Reason for declining..."
              rows={3}
              style={{
                ...inputStyle,
                resize: 'vertical',
                minHeight: 80,
              }}
            />

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                onClick={() => handleDecline(declineId)}
                disabled={actionLoading === declineId}
                style={{
                  flex: 1, padding: '10px', fontSize: 13, fontWeight: 600,
                  background: '#FF4444', color: '#fff', border: 'none',
                  borderRadius: 8, cursor: 'pointer',
                  opacity: actionLoading === declineId ? 0.6 : 1,
                }}
              >
                {actionLoading === declineId ? 'Declining...' : 'Confirm Decline'}
              </button>
              <button
                onClick={() => { setDeclineId(null); setDeclineReason(''); }}
                style={{
                  flex: 1, padding: '10px', fontSize: 13, fontWeight: 600,
                  background: 'transparent', color: textMuted,
                  border: `1px solid ${borderColor}`, borderRadius: 8, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AssignmentManager;
